// Angular
import { inject, Injectable } from '@angular/core';

// Third-party libraries
import { CookieService } from 'ngx-cookie-service';
import { jwtDecode } from 'jwt-decode';

// App environment
import { environment } from '@env/environment';

// App services
import { EncryptionService } from './encryption.service';

@Injectable()
export class TokenService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_ID_KEY = 'user_id';
  private readonly cookieService = inject(CookieService);
  private readonly encryptionService = inject(EncryptionService);

  private readonly cookieOptions = {
    path: '/',
    domain: environment.domain,
    secure: environment.production,
    sameSite: 'Lax' as 'Strict' | 'Lax' | 'None',
  };

  private readonly accessTokenExpiresDays: number = 5 / (24 * 60);
  private readonly refreshTokenExpiresDays: number = 9 / 24;
  private readonly useEncryption: boolean = environment.production;

  private userId: number | null = null;

  getAccessToken(): string | null {
    const token = this.cookieService.get(this.ACCESS_TOKEN_KEY);
    if (!token) {
      return null;
    }
    return this.useEncryption ? this.encryptionService.decrypt(token) : token;
  }

  getRefreshToken(): string | null {
    const token = this.cookieService.get(this.REFRESH_TOKEN_KEY);
    if (!token) {
      return null;
    }
    return this.useEncryption ? this.encryptionService.decrypt(token) : token;
  }

  setTokens(accessToken: string, refreshToken: string): void {
    const encryptedAccessToken = this.useEncryption
      ? this.encryptionService.encrypt(accessToken)
      : accessToken;
    const encryptedRefreshToken = this.useEncryption
      ? this.encryptionService.encrypt(refreshToken)
      : refreshToken;

    this.cookieService.set(
      this.ACCESS_TOKEN_KEY,
      encryptedAccessToken,
      this.accessTokenExpiresDays,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      this.cookieOptions.sameSite,
    );

    this.cookieService.set(
      this.REFRESH_TOKEN_KEY,
      encryptedRefreshToken,
      this.refreshTokenExpiresDays,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      this.cookieOptions.sameSite,
    );

    this.storeUserId(this.extractUserId(accessToken));
  }

  setAccessToken(accessToken: string): void {
    const encryptedToken = this.useEncryption
      ? this.encryptionService.encrypt(accessToken)
      : accessToken;
    this.cookieService.set(
      this.ACCESS_TOKEN_KEY,
      encryptedToken,
      this.accessTokenExpiresDays,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      this.cookieOptions.sameSite,
    );

    this.storeUserId(this.extractUserId(accessToken));
  }

  setRefreshToken(refreshToken: string): void {
    const encryptedToken = this.useEncryption
      ? this.encryptionService.encrypt(refreshToken)
      : refreshToken;
    this.cookieService.set(
      this.REFRESH_TOKEN_KEY,
      encryptedToken,
      this.refreshTokenExpiresDays,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      this.cookieOptions.sameSite,
    );
  }

  removeTokens(): void {
    this.cookieService.delete(this.ACCESS_TOKEN_KEY, this.cookieOptions.path, this.cookieOptions.domain);
    this.cookieService.delete(this.REFRESH_TOKEN_KEY, this.cookieOptions.path, this.cookieOptions.domain);
    this.storeUserId(null);
  }

  hasAccessToken(): boolean {
    return this.getAccessToken() !== null;
  }

  hasRefreshToken(): boolean {
    return this.getRefreshToken() !== null;
  }

  getUserId(): number | null {
    if (this.userId != null) {
      return this.userId;
    }

    const fromCookie = this.readUserIdFromCookie();
    if (fromCookie != null) {
      this.userId = fromCookie;
      return fromCookie;
    }

    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    const extracted = this.extractUserId(token);
    this.storeUserId(extracted);
    return extracted;
  }

  private extractUserId(accessToken: string): number | null {
    try {
      const { UserId } = jwtDecode<{ UserId?: number }>(accessToken);
      return UserId ?? null;
    } catch {
      return null;
    }
  }

  private readUserIdFromCookie(): number | null {
    const raw = this.cookieService.get(this.USER_ID_KEY);
    if (!raw) {
      return null;
    }

    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }

  private storeUserId(userId: number | null): void {
    this.userId = userId;

    if (userId == null) {
      this.cookieService.delete(this.USER_ID_KEY, this.cookieOptions.path, this.cookieOptions.domain);
      return;
    }

    this.cookieService.set(
      this.USER_ID_KEY,
      String(userId),
      this.accessTokenExpiresDays,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      this.cookieOptions.sameSite,
    );
  }
}
